/* CT-2: a contact of the counterparty — create and edit, one modal (`ContactsTab.js` opens it).

     POST  excalibur/counterparty-contacts/       {counterparty, …every field, channels}
     PATCH excalibur/counterparty-contacts/:id/   {…only what changed}

   Both answer the saved contact; the modal then closes with a toast.

   `channels`, when sent, REPLACES every channel of the contact, so it is sent only when the list
   (type, value, comment, in order) differs from the one opened — re-creating them for nothing
   would churn their rows. Each row keeps its `comment`: the form has no field for it, and an
   existing channel's comment goes back untouched or the replace would wipe it.

   "Active" is the API's `is_removed`, inverted: a deactivated contact stays on the list, it is
   just not offered for contact. A primary contact is always active (the API's rule): while
   "Primary" is ticked "Active" is locked, and clearing "Active" clears and locks "Primary" — the
   form's state decides, as the API's own check reads the state after the write. A deleted role already on the contact stays as it is,
   shown by name — it is not re-sent unless it is changed (the API refuses to ASSIGN a deleted one).

   A 400 marks its fields as the address modal does; `channels: [{value: [...]}, {}]` marks the
   channel row at that index. Save is live on a pristine NEW contact (spec: `canSave = id ? dirty :
   true`). */

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Col, Flex, Form, Input, Modal, Row, Select, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import { COUNTERPARTIES_PAGE, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call } from "../../util/action";
import { translate } from "../../util/util";

import { CHANNEL_ICON, CHANNEL_TYPES, channelLabelKey, channelOwner, duplicateChannelKeys } from "./contactChannels";

const URL = "excalibur/counterparty-contacts/";
const ROLE_QUERY = { kind: "contact_role" };
const FIELDS = ["first_name", "last_name", "role", "position", "comment", "is_primary", "is_removed"];
const REQUIRED = ["first_name", "last_name"];
const fieldId = key => `cp-ct-${key}`;
const channelId = key => `cp-ct-channel-${key}`;
const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

let seq = 0;
const rowKey = () => `ch${++seq}`;

const toForm = row => ({
  first_name: row?.first_name || "",
  last_name: row?.last_name || "",
  role: row?.role || null,
  position: row?.position || "",
  comment: row?.comment || "",
  is_primary: !!row?.is_primary,
  is_active: !row?.is_removed,
  channels: (row?.channels || []).map(ch => ({ key: rowKey(), channel_type: ch.channel_type, value: ch.value, comment: ch.comment || "" })),
});

const channelsPayload = channels => channels.map(ch => ({ channel_type: ch.channel_type, value: ch.value.trim(), comment: ch.comment || "" }));

const toPayload = form => ({
  first_name: form.first_name.trim(),
  last_name: form.last_name.trim(),
  role: idOf(form.role) ?? null,
  position: form.position.trim(),
  comment: form.comment.trim(),
  is_primary: form.is_primary,
  is_removed: !form.is_active,
  channels: channelsPayload(form.channels),
});

export const contactName = c => [c?.last_name, c?.first_name].filter(Boolean).join(" ");

export default function ContactModal({ row, counterpartyId, lang, otherPrimary, others = [], onClose, onSaved }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const toast = { position: "top-right" };

  const isNew = !row?.id;
  const [form, setForm] = useState(() => toForm(row));
  /* The opened contact in payload shape — what the dirty check and the changed-keys PATCH compare against. */
  const [baseline] = useState(() => toPayload(toForm(row)));
  const [errors, setErrors] = useState({});
  const [channelErrors, setChannelErrors] = useState({});
  const [busy, setBusy] = useState(false);
  /* The channels as opened, by row key — what "added or changed in this edit" is measured against
     (`baseline` is built by its own `toForm`, so its row keys differ). */
  const [initialChannels] = useState(() => new Map(form.channels.map(ch => [ch.key, ch])));
  /* Repeats are flagged once a save has been refused for them, and then follow the form live: fixing
     or removing either row of a pair clears the mark without another save. */
  const [showChannelChecks, setShowChannelChecks] = useState(false);
  const lastSent = useRef([]);
  const isChanged = useCallback(
    ch => {
      const was = initialChannels.get(ch.key);
      return !was || was.channel_type !== ch.channel_type || was.value.trim() !== ch.value.trim();
    },
    [initialChannels]
  );
  const repeats = useMemo(() => duplicateChannelKeys(form.channels, isChanged), [form.channels, isChanged]);
  /* Row key → the other active contact of this counterparty that already has the channel. */
  const taken = useMemo(
    () =>
      new Map(
        form.channels
          .filter(isChanged)
          .map(ch => [ch.key, channelOwner(ch, others)])
          .filter(([, owner]) => owner)
      ),
    [form.channels, others, isChanged]
  );
  /* The text under a channel row: the API's refusal first, then a repeat on this contact, then a
     match on another one. */
  const channelError = key => {
    if (channelErrors[key]) return t(channelErrors[key]);
    if (!showChannelChecks) return null;
    if (repeats.has(key)) return t("ct_channel_duplicate");
    if (taken.has(key)) return t("ct_channel_taken").replace("{name}", contactName(taken.get(key)));
    return null;
  };

  const payload = useMemo(() => toPayload(form), [form]);
  const dirty = JSON.stringify(payload) !== JSON.stringify(baseline);
  const canSave = isNew ? true : dirty;

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: null } : prev));
  };
  const setChannel = (key, patch) => {
    setForm(prev => ({ ...prev, channels: prev.channels.map(ch => (ch.key === key ? { ...ch, ...patch } : ch)) }));
    setChannelErrors(prev => (prev[key] ? { ...prev, [key]: null } : prev));
  };
  const setActive = checked => setForm(prev => ({ ...prev, is_active: checked, is_primary: checked ? prev.is_primary : false }));
  const addChannel = () => setForm(prev => ({ ...prev, channels: [...prev.channels, { key: rowKey(), channel_type: "phone", value: "", comment: "" }] }));
  const removeChannel = key => setForm(prev => ({ ...prev, channels: prev.channels.filter(ch => ch.key !== key) }));

  /* A 400 body, read raw: `parseErrors` flattens arrays to text and would lose the per-channel list. */
  const refused = response =>
    response
      .json()
      .catch(() => ({}))
      .then(body => {
        const fieldErrs = {};
        FIELDS.forEach(key => {
          if (body && body[key])
            fieldErrs[key] =
              REQUIRED.includes(key) && !payload[key]
                ? "ad_required"
                : key === "is_removed"
                ? "ct_primary_not_deactivated"
                : key === "is_primary" && payload.is_removed
                ? "ct_deactivated_not_primary"
                : "ad_field_invalid";
        });
        const chanErrs = {};
        if (Array.isArray(body?.channels)) {
          body.channels.forEach((err, index) => {
            const sent = lastSent.current[index];
            if (sent && err && Object.keys(err).length) chanErrs[sent] = "ct_channel_invalid";
          });
        }
        setErrors(fieldErrs);
        setChannelErrors(chanErrs);
        const first = Object.keys(fieldErrs)[0];
        const firstChannel = Object.keys(chanErrs)[0];
        setTimeout(() => document.getElementById(first ? fieldId(first) : firstChannel ? channelId(firstChannel) : "")?.focus(), 0);
        const known = Object.keys(fieldErrs).length + Object.keys(chanErrs).length;
        if (!known) Alert.warning(t("edit_error_msg"), toast);
      });

  const submit = () => {
    /* The same channel twice — on this contact or on another active one — is refused here, before
       any request. */
    if (repeats.size || taken.size) {
      setShowChannelChecks(true);
      const first = form.channels.find(ch => repeats.has(ch.key) || taken.has(ch.key));
      setTimeout(() => document.getElementById(channelId(first.key))?.focus(), 0);
      return;
    }
    /* Edit sends the changed keys only — `channels` included only when the list differs. */
    const post = isNew
      ? { counterparty: counterpartyId, ...payload }
      : Object.fromEntries(
          Object.keys(payload)
            .filter(k => JSON.stringify(payload[k]) !== JSON.stringify(baseline[k]))
            .map(k => [k, payload[k]])
        );
    lastSent.current = form.channels.map(ch => ch.key);
    setBusy(true);
    setErrors({});
    setChannelErrors({});
    call({ method: isNew ? "POST" : "PATCH", url: isNew ? URL : `${URL}${row.id}/`, post }, response => {
      setBusy(false);
      if (response?.id) {
        onSaved(response);
        Alert.success(t("ct_saved_msg"), toast);
        onClose();
        return;
      }
      /* A 404 (the contact is gone) comes back parsed, not as a 400 — say so rather than nothing. */
      if (typeof response?.json === "function") refused(response);
      else if (response?.detail) Alert.warning(t("edit_error_msg"), toast);
    });
  };

  const item = (key, labelKey, extra = {}) => ({
    label: t(labelKey),
    htmlFor: fieldId(key),
    required: REQUIRED.includes(key),
    ...extra,
    ...(errors[key] ? { validateStatus: "error", help: t(errors[key]) } : {}),
  });

  const typeOptions = CHANNEL_TYPES.map(type => {
    const Icon = CHANNEL_ICON[type];
    return {
      value: type,
      label: (
        <Flex align="center" gap={8}>
          <Icon aria-hidden />
          {t(channelLabelKey(type))}
        </Flex>
      ),
    };
  });

  const footer = [
    <Button key="cancel" onClick={onClose}>
      {translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
    </Button>,
    <Button key="save" type="primary" loading={busy} disabled={!canSave} onClick={submit}>
      {t("edit_save")}
    </Button>,
  ];

  return (
    <Modal
      open
      title={t(isNew ? "ct_modal_new" : "ct_modal_edit")}
      onCancel={onClose}
      footer={footer}
      width={720}
      style={{ top: 24 }}
      rootClassName="cp-admodal cp-ctmodal">
      <Form component={false} layout="vertical" colon={false}>
        <Flex vertical className="dp-form cp-admodal__form">
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item {...item("first_name", "ct_f_first_name")}>
                <Input id={fieldId("first_name")} maxLength={64} value={form.first_name} onChange={e => set("first_name", e.target.value)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("last_name", "ct_f_last_name")}>
                <Input id={fieldId("last_name")} maxLength={64} value={form.last_name} onChange={e => set("last_name", e.target.value)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("role", "ct_f_role", { extra: t("ct_f_role_hint") })}>
                <EntitySelect
                  id={fieldId("role")}
                  entity="contact_role"
                  extraQuery={ROLE_QUERY}
                  value={form.role?.id}
                  seed={form.role}
                  placeholder={t("ct_f_role")}
                  onChange={value => set("role", value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("position", "ct_f_position")}>
                <Input id={fieldId("position")} maxLength={128} value={form.position} onChange={e => set("position", e.target.value)} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item {...item("comment", "doc_comment")}>
            <Input.TextArea id={fieldId("comment")} rows={2} value={form.comment} onChange={e => set("comment", e.target.value)} />
          </Form.Item>

          <Form.Item label={t("ct_f_channels")} extra={t("ct_channels_hint")}>
            <Flex vertical gap={8}>
              {form.channels.map(ch => (
                <div key={ch.key} className="cp-ctch">
                  <Select
                    className="cp-ctch__type"
                    value={ch.channel_type}
                    options={typeOptions}
                    aria-label={`${t("ct_f_channels")}: ${t("ad_col_type")}`}
                    onChange={value => setChannel(ch.key, { channel_type: value })}
                  />
                  <Input
                    id={channelId(ch.key)}
                    className="cp-ctch__value"
                    status={channelError(ch.key) ? "error" : undefined}
                    maxLength={256}
                    value={ch.value}
                    placeholder={t("ct_channel_value")}
                    aria-label={`${t(channelLabelKey(ch.channel_type))}: ${t("ct_channel_value")}`}
                    onChange={e => setChannel(ch.key, { value: e.target.value })}
                  />
                  <Button
                    className="cp-ctch__remove"
                    icon={<DeleteOutlined />}
                    aria-label={t("ct_remove_channel")}
                    title={t("ct_remove_channel")}
                    onClick={() => removeChannel(ch.key)}
                  />
                  {channelError(ch.key) ? (
                    <Typography.Text type="danger" className="cp-ctch__error">
                      {channelError(ch.key)}
                    </Typography.Text>
                  ) : null}
                </div>
              ))}
              <div>
                <Button icon={<PlusOutlined />} onClick={addChannel}>
                  {t("ct_add_channel")}
                </Button>
              </div>
            </Flex>
          </Form.Item>

          <Form.Item
            {...item("is_primary", "ct_f_primary")}
            label={null}
            extra={
              !form.is_active
                ? t("ct_deactivated_not_primary")
                : form.is_primary && otherPrimary && otherPrimary.id !== row?.id
                ? t("ct_primary_takes").replace("{name}", contactName(otherPrimary))
                : null
            }>
            <Checkbox id={fieldId("is_primary")} checked={form.is_primary} disabled={!form.is_active} onChange={e => set("is_primary", e.target.checked)}>
              {t("ct_f_primary")}
            </Checkbox>
          </Form.Item>
          <Form.Item {...item("is_removed", "ct_f_active")} label={null} extra={t(form.is_primary ? "ct_primary_not_deactivated" : "ct_active_hint")}>
            <Checkbox id={fieldId("is_removed")} checked={form.is_active} disabled={form.is_primary} onChange={e => setActive(e.target.checked)}>
              {t("ct_f_active")}
            </Checkbox>
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
}
